import { css } from 'aphrodite';
import * as React from 'react';
import { useSalesLiveCoachingConversationsQuery } from '../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { CoachingAdherenceScorecard } from '../../components/CallRecordingDetails/components/CoachingAdherenceScorecard';
import { styleSheet } from './styles';
import { usePhoneCallForConversation } from './usePhoneCallForConversation';

interface IProps {
	conversationId: string | null;
}

/**
 * Renders the coaching ADHERENCE scorecard for the selected conversation, below the transcript on
 * the Coffee AI coaching review detail pane.
 *
 * The conversation -> scored phone call link is resolved client-side via
 * `usePhoneCallForConversation` (see that hook for the date-window/pageSize matching limitation).
 * The rep id and creation date come from the already-cached conversation list
 * (`useSalesLiveCoachingConversationsQuery`, deduped by react-query), so reading them here issues
 * no extra request.
 *
 * `CoachingAdherenceScorecard` itself short-circuits to `null` for users without manager
 * permissions, so on this internal review surface the scorecard only appears for managers/admins.
 */
export function AdherencePanel({ conversationId }: IProps) {
	const { data, isLoading: conversationsLoading } = useSalesLiveCoachingConversationsQuery();
	// Look the selected conversation up among the loaded pages — it was selected from this same list,
	// so it is always present once loaded.
	const conversations = data?.conversations;

	const conversation = React.useMemo(
		() => (conversationId ? conversations?.find(c => c.id === conversationId) : undefined),
		[conversations, conversationId]
	);

	const {
		data: phoneCall,
		isLoading,
		isError,
	} = usePhoneCallForConversation({
		conversationId,
		repUserId: conversation?.creator?.id,
		conversationDate: conversation?.creationDate,
		enabled: Boolean(conversationId),
	});

	// Nothing selected: render nothing (the transcript panel owns the "select a conversation" prompt).
	if (!conversationId) {
		return null;
	}

	// While the conversation list is still resolving, `conversation` (and thus repUserId) is
	// undefined, which disables the phone-call query — without this guard we'd briefly flash the
	// "no analysis found" note before the real query runs.
	if (conversationsLoading || isLoading) {
		return (
			<section className={css(styleSheet.adherence)} aria-label='Coaching adherence'>
				<h3 className={css(styleSheet.adherenceTitle)}>Coaching Adherence</h3>
				<div className={css(styleSheet.adherenceSpinner)}>
					<LoadingSpinner type='small' />
				</div>
			</section>
		);
	}

	// Errors are surfaced by the query's `meta.toastErrorMessage`; fall through to the muted note.
	const hasResult = !isError && Boolean(phoneCall?.coachingAdherenceResult);

	return (
		<section className={css(styleSheet.adherence)} aria-label='Coaching adherence'>
			<h3 className={css(styleSheet.adherenceTitle)}>Coaching Adherence</h3>
			{hasResult && phoneCall ? (
				<CoachingAdherenceScorecard phoneCall={phoneCall} />
			) : (
				<p className={css(styleSheet.adherenceNote)}>No coaching adherence analysis found for this conversation.</p>
			)}
		</section>
	);
}
